import { Table } from '@intercom/surge';
import { Button } from '@intercom/surge';
import { SmallDownArrowIcon } from '@intercom/surge/icons';
import { SmallUpArrowIcon } from '@intercom/surge/icons';
import {
  ChartSchema,
  ChartDefinition,
  CellType,
  RowType,
  ColumnDefinition,
  Timezone,
  HeaderDefinition,
} from '@/features/report-snapshots/api/schema';
import { useMemo, useState, useCallback } from 'react';
import { useReactTable, getCoreRowModel, flexRender, CellContext } from '@tanstack/react-table';
import { Trans } from '@lingui/react/macro';
import { formatValue } from '@/features/report-snapshots/formatters/main';
import {
  formatCellValue,
  formatComparisonValue,
} from '@/features/report-snapshots/formatters/cell-value';
import { useFormatters } from '@/features/report-snapshots/formatters/use-formatters';
import {
  createSchemaComparator,
  ComparisonResult,
} from '@/features/report-snapshots/utils/schema-comparator';
import { cn } from '@intercom/surge';
import { ChartTitle } from '@/features/report-snapshots/components/charts/chart-title';
import { isEmptyChart } from '@/features/report-snapshots/utils/chart-data-helpers';
import { EmptyStateChart } from '@/features/report-snapshots/components/charts/empty-state-chart';
import { getTargetStatus } from '@/features/report-snapshots/utils/reporting-table';

function renderComparisonValue(
  comparisonResult: ComparisonResult | null,
  columnDefinition: ColumnDefinition,
  formatters: ReturnType<typeof useFormatters>,
) {
  if (comparisonResult === null) {
    return null;
  }

  if (comparisonResult.unchanged) {
    return <span className="text-muted pl-1 flex">-</span>;
  }

  const formattedComparisonValue = formatComparisonValue(
    comparisonResult.delta ?? 0,
    columnDefinition,
    formatters,
  );
  return (
    <span
      className={cn(
        'flex items-center',
        comparisonResult.hasImproved ? 'text-success' : 'text-error',
      )}
    >
      {comparisonResult.hasIncreased ? <SmallUpArrowIcon /> : <SmallDownArrowIcon />}
      <span>{formattedComparisonValue}</span>
    </span>
  );
}

function buildColumn(
  id: string,
  tableDefinition: { header: string; columnDefinition: ColumnDefinition },
  chartDefinition: ChartDefinition,
  timezone: Timezone,
  formatters: ReturnType<typeof useFormatters>,
  schemaComparator: ReturnType<typeof createSchemaComparator> | null,
) {
  const { header, columnDefinition } = tableDefinition;
  return {
    accessorKey: id,
    header,
    cell: ({ cell }: CellContext<RowType, CellType>) => {
      const interval = chartDefinition.viewByTimeInterval ?? 'week';
      const { target } = chartDefinition.visualizationOptions;
      const rawValue = cell.getValue();
      const formattedCellValue = formatCellValue(
        columnDefinition,
        formatters,
        timezone,
        rawValue,
        interval,
      ) as string;

      const colIndex = cell.column.getIndex();
      const rowIndex = cell.row.index;
      let comparisonResult = null;

      if (colIndex !== 0 && schemaComparator) {
        comparisonResult = schemaComparator.values(rowIndex, colIndex);
      }

      const targetStatus = getTargetStatus(rawValue, target, columnDefinition, formatters);
      return (
        <div className={cn('flex p-3', targetStatus)}>
          <span className="min-w-12">{formattedCellValue}</span>
          {renderComparisonValue(comparisonResult, columnDefinition, formatters)}
        </div>
      );
    },
  };
}

function buildHeader(
  header: HeaderDefinition,
  formatters: ReturnType<typeof useFormatters>,
  chartDefinition: ChartDefinition,
  timezone: Timezone,
) {
  const { value, unit } = header;
  const interval = chartDefinition.segmentByTimeInterval ?? 'week';

  switch (unit) {
    case 'text':
      return formatValue(value, unit);
    case 'timestamp':
      return formatters.timestamp(interval, value as number, timezone);
    default:
      return String(value);
  }
}

function renderSummaryRow(
  chartSchema: ChartSchema,
  chartDefinition: ChartDefinition,
  formatters: ReturnType<typeof useFormatters>,
  timezone: Timezone,
  schemaComparator: ReturnType<typeof createSchemaComparator> | null,
) {
  const { summaryRow, columnDefinitions } = chartSchema;

  if (summaryRow === null) {
    return null;
  }

  return (
    <Table.Row className="border-b-4 font-semibold">
      {columnDefinitions.map((columnDef) => {
        const summaryValue = summaryRow[columnDef.position];

        const formattedValue =
          columnDef.position === 0
            ? (summaryValue as string)
            : (formatCellValue(
                columnDef,
                formatters,
                timezone,
                summaryValue,
                chartDefinition.viewByTimeInterval ?? 'week',
              ) as string);

        let formattedResult = null;
        if (columnDef.position !== 0 && schemaComparator) {
          formattedResult = schemaComparator.summaryRow(columnDef.position);
        }

        return (
          <Table.Cell
            key={columnDef.position}
            data-testid={`table-summary-cell-${String(columnDef.position)}`}
          >
            <div className="flex">
              <span className="min-w-12">{formattedValue}</span>
              {renderComparisonValue(formattedResult, columnDef, formatters)}
            </div>
          </Table.Cell>
        );
      })}
    </Table.Row>
  );
}

export const ReportingTable = ({
  chartSchemas,
  chartDefinition,
  timezone,
}: {
  chartSchemas: ChartSchema[];
  chartDefinition: ChartDefinition;
  timezone: Timezone;
}) => {
  const formatters = useFormatters();

  const isTimeComparisonEnabled = chartDefinition.visualizationOptions.showTimeComparison;

  const chartSchema = isTimeComparisonEnabled
    ? (chartSchemas[1] ?? chartSchemas[0])
    : chartSchemas[0];

  const schemaComparator =
    isTimeComparisonEnabled && chartSchemas.length > 1
      ? createSchemaComparator(chartSchema, chartSchemas[0])
      : null;

  // Pagination state - reset when chart data changes
  const chartDataKey = useMemo(() => JSON.stringify(chartSchema.values), [chartSchema.values]);
  const [displayedRowCount, setDisplayedRowCount] = useState(10);
  const [lastChartDataKey, setLastChartDataKey] = useState(chartDataKey);

  // Reset pagination when chart data changes
  if (lastChartDataKey !== chartDataKey) {
    setLastChartDataKey(chartDataKey);
    setDisplayedRowCount(10);
  }

  const allRows = useMemo(() => chartSchema.values, [chartSchema.values]);
  const hasMoreRows = useMemo(
    () => displayedRowCount < allRows.length,
    [displayedRowCount, allRows.length],
  );
  const displayedRows = useMemo(
    () => allRows.slice(0, displayedRowCount),
    [allRows, displayedRowCount],
  );

  const loadMoreRows = useCallback(() => {
    setDisplayedRowCount((prev) => Math.min(prev + 10, allRows.length));
  }, [allRows.length]);

  const columns = useMemo(
    () =>
      (chartSchema.headers ?? [])
        .map((header, index) => ({
          header: buildHeader(header, formatters, chartDefinition, timezone),
          columnDefinition: chartSchema.columnDefinitions[index],
        }))
        .map((tableDefinition, index) =>
          buildColumn(
            index.toString(),
            tableDefinition,
            chartDefinition,
            timezone,
            formatters,
            schemaComparator,
          ),
        ),
    [
      chartSchema.headers,
      chartSchema.columnDefinitions,
      formatters,
      chartDefinition,
      timezone,
      schemaComparator,
    ],
  );

  const table = useReactTable({
    data: displayedRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isEmptyChart(chartSchemas, 'tabular')) {
    return (
      <div className="flex-1 min-h-0">
        <ChartTitle chartSchema={chartSchema} chartDefinition={chartDefinition} />
        <EmptyStateChart />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <ChartTitle chartSchema={chartSchema} chartDefinition={chartDefinition} />
      <Table rootClassName="flex-1 min-h-0">
        <Table.Header className="sticky top-0 bg-base-module z-10">
          <Table.Row>
            {table.getHeaderGroups().map((headerGroup) => {
              return headerGroup.headers.map((header) => {
                return (
                  <Table.HeaderCell key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Table.HeaderCell>
                );
              });
            })}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {renderSummaryRow(chartSchema, chartDefinition, formatters, timezone, schemaComparator)}
          {table.getRowModel().rows.map((row) => (
            <Table.Row key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Table.Cell
                  key={cell.id}
                  data-testid={`reporting-table-cell-${cell.id}`}
                  className="p-0"
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {hasMoreRows && (
        <div className="flex justify-center items-center pt-4 shrink-0">
          <Button variant="secondary" onClick={loadMoreRows} data-testid="load-more-button">
            <Trans>Load more</Trans>
          </Button>
        </div>
      )}
    </div>
  );
};
